import { DocumentNode } from 'graphql';
import { Download, Trash2 } from 'react-feather';
import { addYears } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import isToday from 'date-fns/isToday';
import isYesterday from 'date-fns/isYesterday';

import { GetExportsQuery as GetExports } from '__generated__/graphql';

import { useNotifier } from '../../common/Notifications/NotificationProvider';
import DeleteExportMutation from './DeleteExportMutation';
import GetExportsQuery from './GetExportsQuery';
import HideExportProfileMutation from './HideExportProfileMutation';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useMutation from '../../api/useMutation';

type Export = GetExports['session']['user']['recent_exports'][0];
type ExportProfile = GetExports['project']['export_profiles'][0];

export interface ExportsModalProps {
  recentExports: Export[];
  exportProfiles: ExportProfile[];
  mailchimpExports: Export[];
  close?: () => void;
  initialTab?: 'recent_exports' | 'export_profiles' | 'mailchimp';
  refetchQueries?: DocumentNode[];
}

const ExportsModal = ({
  recentExports, exportProfiles, mailchimpExports, initialTab = 'recent_exports', close,
  refetchQueries = [GetExportsQuery],
}: ExportsModalProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate, formatNumber } = useLocale();
  const notifier = useNotifier();

  const [activeTab, setActiveTab] = useState(initialTab);

  const [
    deleteExportProfile, { loading: deletingExportProfile },
  ] = useMutation(
    HideExportProfileMutation, {
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.export_profile_deleted'));
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  return (
    <UI.Modal maxWidth={800} close={close}>
      <UI.FormGrid>
        <UI.Tabs sc={{ border: true }}>
          <UI.Tab
            to=""
            as="span"
            role="button"
            className={activeTab === 'recent_exports' ? 'active' : ''}
            onClick={() => setActiveTab('recent_exports')}
            sc={{ pt: 0 }}
          >
            {t('recent_exports')}
            {' '}
            <UI.Small>
              <UI.Badge sc={{ color: 'gray.500', brand: 'gray.100' }}>
                {formatNumber(recentExports.length)}
              </UI.Badge>
            </UI.Small>
          </UI.Tab>
          <UI.Tab
            to=""
            as="span"
            role="button"
            className={activeTab === 'export_profiles' ? 'active' : ''}
            onClick={() => setActiveTab('export_profiles')}
            sc={{ pt: 0 }}
          >
            {t('export_profiles')}
            {' '}
            <UI.Small>
              <UI.Badge sc={{ color: 'gray.500', brand: 'gray.100' }}>
                {formatNumber(exportProfiles.length)}
              </UI.Badge>
            </UI.Small>
          </UI.Tab>
          {mailchimpExports.length > 0 && (
            <UI.Tab
              to=""
              as="span"
              role="button"
              className={activeTab === 'mailchimp' ? 'active' : ''}
              onClick={() => setActiveTab('mailchimp')}
              sc={{ pt: 0 }}
            >
              {t('mailchimp')}
              {' '}
              <UI.Small>
                <UI.Badge sc={{ color: 'gray.500', brand: 'gray.100' }}>
                  {formatNumber(mailchimpExports.length)}
                </UI.Badge>
              </UI.Small>
            </UI.Tab>
          )}
        </UI.Tabs>

        {activeTab === 'recent_exports' && (
          <>
            <UI.Div>
              {t('recent_exports_description')}
            </UI.Div>
            {recentExports.length > 0 && (
              <UI.Table sc={{ borderedRows: true }}>
                <UI.THead>
                  <UI.TR>
                    <UI.TH>
                      <UI.Small>
                        {t('export')}
                      </UI.Small>
                    </UI.TH>
                    <UI.TH style={{ width: 160 }}>
                      <UI.Small>
                        {t('status')}
                      </UI.Small>
                    </UI.TH>
                    <UI.TH style={{ width: 160 }}>
                      <UI.Small>
                        {t('date')}
                      </UI.Small>
                    </UI.TH>
                  </UI.TR>
                </UI.THead>
                <UI.TBody>
                  {recentExports.map((spreadsheetExport) => {
                    const date = parseDate(spreadsheetExport.created_at, {
                      format: 'internal_date_time', timezone: 'UTC',
                    });
                    const time = formatDate(date, { format: 'display_time' });

                    return (
                      <UI.TR style={{ lineHeight: '20px' }} key={spreadsheetExport.id}>
                        <UI.TD sc={{ muted: !spreadsheetExport.finished, py: 1.5 }}>
                          {!spreadsheetExport.finished && spreadsheetExport.title}
                          {spreadsheetExport.finished && (
                            <UI.A
                              href={spreadsheetExport.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              sc={{ secondary: true }}
                              style={{ fontWeight: 500 }}
                            >
                              <UI.Icon>
                                <Download />
                              </UI.Icon>
                              {' '}
                              {spreadsheetExport.title}
                            </UI.A>
                          )}
                        </UI.TD>
                        <UI.TD sc={{ small: true, py: 1.25 }}>
                          {!spreadsheetExport.finished && !spreadsheetExport.failed && (
                            <UI.Label sc={{ fontWeight: 500, background: 'gray.100', color: 'gray.700' }}>
                              <UI.Loader sc={{ size: 12, mb: 0.5, brand: 'gray.700' }} />
                              {' '}
                              {spreadsheetExport.progress > 0 ? `${spreadsheetExport.progress}%` : t('in_progress')}
                            </UI.Label>
                          )}
                          {spreadsheetExport.finished && (
                            <UI.Label sc={{ fontWeight: 500, background: 'primary.25', color: 'primary.600' }}>
                              {t('done')}
                            </UI.Label>
                          )}
                          {spreadsheetExport.failed && (
                            <UI.Label sc={{ fontWeight: 500, background: 'error.100', color: 'error.700' }}>
                              {t('failed')}
                            </UI.Label>
                          )}
                        </UI.TD>
                        <UI.TD sc={{ small: true, muted: true, py: 1.5 }}>
                          {!isYesterday(date) && !isToday(date) && formatDate(date, {
                            format: 'display_compact_date_time',
                          })}
                          {isYesterday(date) && `${t('yesterday')}, ${time}`}
                          {isToday(date) && `${t('today')}, ${time}`}
                        </UI.TD>
                      </UI.TR>
                    );
                  })}
                </UI.TBody>
              </UI.Table>
            )}
            {recentExports.length === 0 && (
              <UI.Div sc={{ muted: true }}>
                {t('no_exports_description')}
              </UI.Div>
            )}
          </>
        )}

        {activeTab === 'export_profiles' && (
          <>
            <UI.Div>
              {t('export_profiles_description')}
            </UI.Div>
            {exportProfiles.length > 0 && (
              <UI.Table sc={{ borderedRows: true }}>
                <UI.THead>
                  <UI.TR>
                    <UI.TH>
                      <UI.Small>
                        {t('export_profile')}
                      </UI.Small>
                    </UI.TH>
                    <UI.TH>
                      <UI.Small>
                        {t('event')}
                      </UI.Small>
                    </UI.TH>
                    <UI.TH style={{ width: 120 }}>
                      <UI.Small>
                        {t('type')}
                      </UI.Small>
                    </UI.TH>
                    <UI.TH style={{ width: 100 }}>
                      <UI.Small>
                        {t('date')}
                      </UI.Small>
                    </UI.TH>
                    <UI.TH style={{ width: 100 }} />
                  </UI.TR>
                </UI.THead>
                <UI.TBody>
                  {exportProfiles.map((exportProfile) => (
                    <UI.TR style={{ lineHeight: '20px' }} key={exportProfile.id}>
                      <UI.TD sc={{ py: 1.5, fontWeight: 500 }}>
                        {exportProfile.title}
                      </UI.TD>
                      <UI.TD sc={{ small: true, py: 1.5 }}>
                        {exportProfile.event?.title}
                      </UI.TD>
                      <UI.TD sc={{ small: true, py: 1.5 }}>
                        {t(exportProfile.type)}
                      </UI.TD>
                      <UI.TD sc={{ small: true, muted: true, py: 1.5 }}>
                        {formatDate(parseDate(exportProfile.created_at), { format: 'display_compact_date' })}
                      </UI.TD>
                      <UI.TD sc={{ small: true, textAlign: 'right', py: 0.5 }}>
                        <UI.ConfirmButton
                          onConfirm={() => deleteExportProfile({ variables: { id: exportProfile.id } })}
                          sc={{ size: 'sm', blank: true, color: 'error' }}
                          loading={deletingExportProfile}
                          render={(start, disabled) => (
                            <UI.Button
                              onClick={start}
                              sc={{ size: 'sm', blank: true, color: 'gray.400' }}
                              disabled={disabled}
                              aria-label={t('delete_name', { name: exportProfile.title })}
                            >
                              <UI.Icon>
                                <Trash2 />
                              </UI.Icon>
                            </UI.Button>
                          )}
                        />
                      </UI.TD>
                    </UI.TR>
                  ))}
                </UI.TBody>
              </UI.Table>
            )}
            {exportProfiles.length === 0 && (
              <UI.Div sc={{ muted: true }}>
                {t('no_export_profiles_description')}
              </UI.Div>
            )}
          </>
        )}

        {activeTab === 'mailchimp' && (
          <>
            <UI.Div>
              {t('mailchimp_exports_description')}
            </UI.Div>
            {mailchimpExports.length > 0 && (
              <UI.Div>
                <UI.Table sc={{ borderedRows: true }}>
                  <UI.THead>
                    <UI.TR>
                      <UI.TH>
                        <UI.Small>
                          {t('tag')}
                        </UI.Small>
                      </UI.TH>
                      <UI.TH style={{ width: 160 }}>
                        <UI.Small>
                          {t('last_sync')}
                        </UI.Small>
                      </UI.TH>
                      <UI.TH style={{ width: 130 }}>
                        <UI.Small>
                          {t('ends_on')}
                        </UI.Small>
                      </UI.TH>
                      <UI.TH style={{ width: 32 }} />
                    </UI.TR>
                  </UI.THead>
                  <UI.TBody>
                    {mailchimpExports.map((mailchimpExport) => (
                      <MailchimpExportRow
                        export={mailchimpExport}
                        refetchQueries={refetchQueries}
                        key={mailchimpExport.id}
                      />
                    ))}
                  </UI.TBody>
                </UI.Table>
              </UI.Div>
            )}
            {mailchimpExports.length === 0 && (
              <UI.Div sc={{ muted: true }}>
                {t('no_exports_description')}
              </UI.Div>
            )}
          </>
        )}
      </UI.FormGrid>
    </UI.Modal>
  );
};

interface MailchimpExportProps {
  export: Export;
  refetchQueries?: DocumentNode[];
}

const MailchimpExportRow = ({ export: mailchimpExport, refetchQueries }: MailchimpExportProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();
  const notifier = useNotifier();

  const [deleteExport, { loading }] = useMutation(
    DeleteExportMutation, {
      variables: {
        id: mailchimpExport.id,
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.export_deleted'));
      },
      onError: () => {
        notifier.error(t('something_went_wrong'));
      },
    },
  );

  const until = addYears(parseDate(mailchimpExport.created_at, { timezone: 'UTC' }), 1);

  return (
    <UI.TR style={{ lineHeight: '20px' }}>
      <UI.TD
        sc={{ py: 1.5, muted: !mailchimpExport.finished }}
        style={{ fontWeight: mailchimpExport.finished ? 500 : undefined }}
      >
        <UI.Icon>
          <UI.Icons.Mailchimp />
        </UI.Icon>
        {' '}
        {mailchimpExport.title}
      </UI.TD>
      <UI.TD sc={{ small: true, py: 1 }}>
        {mailchimpExport.generated_at && (
          <UI.Span sc={{ muted: true }}>
            {formatDate(
              parseDate(mailchimpExport.generated_at, { timezone: 'UTC' }),
              { format: 'display_compact_date_time' },
            )}
          </UI.Span>
        )}
        {!mailchimpExport.generated_at && (
          <UI.Label sc={{ fontWeight: 500, background: 'gray.100', color: 'gray.700' }}>
            <UI.Loader sc={{ size: 12, mb: 0.5, brand: 'gray.700' }} />
            {' '}
            {t('in_progress')}
          </UI.Label>
        )}
      </UI.TD>
      <UI.TD sc={{ small: true, muted: true, py: 1.5 }}>
        {formatDate(until, { format: 'display_compact_date' })}
      </UI.TD>
      <UI.TD sc={{ textAlign: 'right' }}>
        {mailchimpExport.finished && (
          <UI.Button
            onClick={() => deleteExport()}
            sc={{ blank: true, color: 'gray.400', size: 'sm', my: -0.25 }}
            disabled={loading}
            aria-label={t('delete_name', { name: mailchimpExport.title })}
          >
            <UI.Icon>
              <Trash2 />
            </UI.Icon>
          </UI.Button>
        )}
      </UI.TD>
    </UI.TR>
  );
};

export default ExportsModal;
